import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import * as CANNON from 'cannon-es'
import { Dice } from './Dice.js'

// ─── Scene ───────────────────────────────────────────────────────────────────
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
scene.background = new THREE.Color('#16213e')

// ─── Physics World ───────────────────────────────────────────────────────────
const world = new CANNON.World()
world.broadphase = new CANNON.NaiveBroadphase()
world.allowSleep = true
world.gravity.set(0, -9.82, 0)
world.solver.iterations = 20

const defaultMaterial = new CANNON.Material('default')
world.defaultContactMaterial = new CANNON.ContactMaterial(defaultMaterial, defaultMaterial, {
    friction: 0.35,
    restitution: 0.55,
})

// ─── Room ─────────────────────────────────────────────────────────────────────
const ROOM = { w: 8, h: 6, d: 8 }
const hw = ROOM.w / 2, hh = ROOM.h / 2, hd = ROOM.d / 2
const WT = 1.0  // thick walls — prevents fast dice from tunneling through

function addPhysicsWall(pos, halfExtents) {
    const body = new CANNON.Body({
        mass: 0,
        material: defaultMaterial,
        shape: new CANNON.Box(new CANNON.Vec3(...halfExtents)),
    })
    body.position.set(...pos)
    world.addBody(body)
}

// Each wall is shifted outward by WT so its inner face sits exactly on the room boundary.
addPhysicsWall([0,          -WT,   0], [hw + WT,      WT, hd + WT])  // floor
addPhysicsWall([0, ROOM.h + WT,   0], [hw + WT,      WT, hd + WT])  // ceiling
addPhysicsWall([-hw - WT,    hh,   0], [WT,      hh + WT, hd + WT])  // left
addPhysicsWall([ hw + WT,    hh,   0], [WT,      hh + WT, hd + WT])  // right
addPhysicsWall([0,           hh, -hd - WT], [hw + WT, hh + WT, WT])  // back
addPhysicsWall([0,           hh,  hd + WT], [hw + WT, hh + WT, WT])  // front

const glassMat = new THREE.MeshStandardMaterial({
    color: '#88bbff', transparent: true, opacity: 0.08,
    side: THREE.DoubleSide, depthWrite: false,
})
const floorMat = new THREE.MeshStandardMaterial({ color: '#1a1a2e', roughness: 0.9 })

const floorMesh = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.w, ROOM.d), floorMat)
floorMesh.rotation.x = -Math.PI / 2
floorMesh.receiveShadow = true
scene.add(floorMesh)

const grid = new THREE.GridHelper(ROOM.w, 8, '#2a3a5e', '#2a3a5e')
grid.position.y = 0.001
scene.add(grid)

const roofMesh = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.w, ROOM.d), glassMat)
roofMesh.rotation.x = Math.PI / 2
roofMesh.position.y = ROOM.h
scene.add(roofMesh)

for (const { size, pos, rot } of [
    { size: [ROOM.d, ROOM.h], pos: [-hw, hh, 0],  rot: [0,  Math.PI / 2, 0] },
    { size: [ROOM.d, ROOM.h], pos: [ hw, hh, 0],  rot: [0, -Math.PI / 2, 0] },
    { size: [ROOM.w, ROOM.h], pos: [0,  hh, -hd], rot: [0, 0, 0] },
    { size: [ROOM.w, ROOM.h], pos: [0,  hh,  hd], rot: [0, Math.PI, 0] },
]) {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(...size), glassMat)
    mesh.position.set(...pos)
    mesh.rotation.set(...rot)
    scene.add(mesh)
}

const edgeLines = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.BoxGeometry(ROOM.w, ROOM.h, ROOM.d)),
    new THREE.LineBasicMaterial({ color: '#4488dd', transparent: true, opacity: 0.55 })
)
edgeLines.position.set(0, hh, 0)
scene.add(edgeLines)

// ─── Lighting ────────────────────────────────────────────────────────────────
scene.add(new THREE.AmbientLight(0xffffff, 1.8))

const dirLight = new THREE.DirectionalLight(0xffffff, 1.5)
dirLight.position.set(4, 10, 6)
dirLight.castShadow = true
dirLight.shadow.mapSize.set(1024, 1024)
dirLight.shadow.camera.near = 1
dirLight.shadow.camera.far = 25
dirLight.shadow.camera.left = -8
dirLight.shadow.camera.right = 8
dirLight.shadow.camera.top = 10
dirLight.shadow.camera.bottom = -2
scene.add(dirLight)

const fillLight = new THREE.PointLight('#2244aa', 0.5, 12)
fillLight.position.set(0, 0.5, 0)
scene.add(fillLight)

// ─── UI refs ──────────────────────────────────────────────────────────────────
const resultEl = document.getElementById('result')
const hintEl   = document.getElementById('hint')

// ─── Stats ────────────────────────────────────────────────────────────────────
let rollCounts = {}
let totalEl
const statsEl = document.getElementById('stats')

function initStats(type) {
    const max = type === 'd6' ? 6 : 20
    rollCounts = {}
    for (let i = 1; i <= max; i++) rollCounts[i] = 0

    statsEl.className = type
    statsEl.innerHTML =
        '<div class="stats-title">Results</div>' +
        '<div id="stats-total">Rolls: 0</div>' +
        '<div id="stats-rows">' +
        Array.from({ length: max }, (_, i) => i + 1).map(f =>
            `<div class="stat-row">` +
            `<span class="face-num">${f}</span>` +
            `<span class="bar-wrap"><span class="bar" id="bar-${f}"></span></span>` +
            `<span class="stat-count" id="cnt-${f}">0</span>` +
            `<span class="stat-pct" id="pct-${f}">—</span>` +
            `</div>`
        ).join('') +
        '</div>'
    totalEl = document.getElementById('stats-total')
}

function updateStats(value) {
    rollCounts[value] = (rollCounts[value] || 0) + 1
    const total = Object.values(rollCounts).reduce((a, b) => a + b, 0)
    if (totalEl) totalEl.textContent = `Rolls: ${total}`
    for (const [f, count] of Object.entries(rollCounts)) {
        const pct = total > 0 ? (count / total) * 100 : 0
        const barEl = document.getElementById(`bar-${f}`)
        const cntEl = document.getElementById(`cnt-${f}`)
        const pctEl = document.getElementById(`pct-${f}`)
        if (barEl) barEl.style.width = `${pct}%`
        if (cntEl) cntEl.textContent = count
        if (pctEl) pctEl.textContent = total > 0 ? `${pct.toFixed(1)}%` : '—'
    }
}

// ─── Dice management ──────────────────────────────────────────────────────────
let diceList     = []
let currentType  = 'd6'
let currentCount = 1
let lastResults  = []

function spreadPositions(count) {
    if (count === 1) return [{ x: 0, y: 2, z: 0 }]
    return Array.from({ length: count }, (_, i) => {
        const angle = (i / count) * Math.PI * 2
        const r = count > 3 ? 1.5 : 1.0
        return { x: Math.cos(angle) * r, y: 2.0 + i * 0.25, z: Math.sin(angle) * r }
    })
}

function createDice(count, type) {
    diceList.forEach(d => d.dispose())
    diceList = []
    autoThrowPending = false

    if (type !== currentType) initStats(type)
    currentType  = type
    currentCount = count
    lastResults  = new Array(count).fill(null)

    resultEl.style.fontSize = count === 1 ? '7rem' : count <= 3 ? '4rem' : '3rem'

    spreadPositions(count).forEach((pos, i) => {
        const d = new Dice({ scene, world, physicsMaterial: defaultMaterial, position: pos, size: 1, type })
        d.onResult = (value) => {
            updateStats(value)
            lastResults[i] = value
            if (lastResults.every(v => v !== null)) {
                resultEl.textContent = count === 1 ? String(lastResults[0]) : lastResults.join('  ')
                resultEl.classList.add('visible')
                if (autoThrowActive) {
                    hintEl.textContent = 'Auto rolling…'
                    scheduleAutoThrow()
                } else {
                    hintEl.textContent = 'Press and drag to roll again'
                }
            }
        }
        diceList.push(d)
    })
}

// ─── Auto-throw ───────────────────────────────────────────────────────────────
let autoThrowActive  = false
let autoThrowPending = false

function throwAllDice(baseDir, basePower) {
    lastResults = new Array(diceList.length).fill(null)
    let thrown = false
    diceList.forEach(d => {
        const angle = Math.atan2(baseDir.z, baseDir.x) + (Math.random() - 0.5) * 0.4
        const dir   = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle))
        const power = Math.max(0.1, Math.min(1, basePower + (Math.random() - 0.5) * 0.2))
        if (d.throwInDirection(dir, power)) thrown = true
    })
    return thrown
}

function scheduleAutoThrow() {
    if (autoThrowPending) return
    autoThrowPending = true
    setTimeout(() => {
        autoThrowPending = false
        if (!autoThrowActive) return
        const angle = Math.random() * Math.PI * 2
        const dir   = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle))
        const power = 0.35 + Math.random() * 0.65
        if (throwAllDice(dir, power)) {
            resultEl.classList.remove('visible')
            hintEl.textContent = 'Rolling…'
        }
    }, 650)
}

function kickAutoThrow() {
    if (!autoThrowActive) return
    if (diceList.every(d => d.isSettled)) {
        scheduleAutoThrow()
    } else {
        setTimeout(kickAutoThrow, 200)
    }
}

const autoBtn = document.getElementById('auto-btn')
autoBtn.addEventListener('click', () => {
    autoThrowActive = !autoThrowActive
    autoBtn.classList.toggle('active', autoThrowActive)
    if (autoThrowActive) kickAutoThrow()
})

// ─── Dice type / count controls ───────────────────────────────────────────────
document.querySelectorAll('.type-btn').forEach(btn => {
    btn.addEventListener('click', () => {
        const type = btn.dataset.type
        if (type === currentType) return
        document.querySelectorAll('.type-btn').forEach(b => b.classList.toggle('active', b === btn))
        createDice(currentCount, type)
    })
})

document.querySelectorAll('.count-btn').forEach(btn => {
    btn.addEventListener('click', () => {
        const count = Number(btn.dataset.count)
        if (count === currentCount) return
        document.querySelectorAll('.count-btn').forEach(b => b.classList.toggle('active', b === btn))
        createDice(count, currentType)
    })
})

// ─── Speed control ────────────────────────────────────────────────────────────
let simSpeed = 1
document.querySelectorAll('.speed-btn').forEach(btn => {
    btn.addEventListener('click', () => {
        simSpeed = Number(btn.dataset.speed)
        document.querySelectorAll('.speed-btn').forEach(b => b.classList.toggle('active', b === btn))
    })
})

// ─── Initial spawn ────────────────────────────────────────────────────────────
initStats('d6')
createDice(1, 'd6')

// ─── 3D Aim Indicator ────────────────────────────────────────────────────────
const AIM_RING_RADIUS = 2.5
const AIM_RING_Y      = 1.5

const aimRingMat = new THREE.MeshBasicMaterial({
    color: '#4488ff', transparent: true, opacity: 0.5, side: THREE.DoubleSide,
})
const aimRing = new THREE.Mesh(new THREE.TorusGeometry(AIM_RING_RADIUS, 0.04, 8, 64), aimRingMat)
aimRing.rotation.x = Math.PI / 2
aimRing.visible = false
scene.add(aimRing)

const aimDotMat = new THREE.MeshBasicMaterial({ color: '#ffffff' })
const aimDot = new THREE.Mesh(new THREE.SphereGeometry(0.1, 12, 12), aimDotMat)
aimDot.visible = false
scene.add(aimDot)

const aimArrow = new THREE.ArrowHelper(new THREE.Vector3(0, 1, 0), new THREE.Vector3(), 1, '#88ccff', 0.3, 0.15)
aimArrow.visible = false
scene.add(aimArrow)

const decayLineGeo = new THREE.BufferGeometry()
const decayLineMat = new THREE.LineDashedMaterial({ color: '#88ccff', transparent: true, opacity: 0.6, dashSize: 0.15, gapSize: 0.1 })
const decayLine = new THREE.Line(decayLineGeo, decayLineMat)
decayLine.visible = false
scene.add(decayLine)

function setAimVisible(v) {
    aimRing.visible = aimDot.visible = aimArrow.visible = decayLine.visible = v
}

function computeDecayArc(dotPos, dir, power) {
    const speed  = 4 + power * 10
    const upward = 6 + power * 4
    const g = -9.82
    const h = AIM_RING_Y
    const discriminant = upward * upward - 4 * 4.91 * h
    if (discriminant < 0) return []
    const t1  = (upward - Math.sqrt(discriminant)) / (2 * 4.91)
    const vy1 = upward + g * t1
    const vx1 = dir.x * speed
    const vz1 = dir.z * speed
    const pts = [dotPos.clone()]
    for (let i = 1; i <= 12; i++) {
        const t  = (i / 12) * 0.35
        const py = dotPos.y + vy1 * t + 0.5 * g * t * t
        if (py < 0.1) break
        pts.push(new THREE.Vector3(dotPos.x + vx1 * t, py, dotPos.z + vz1 * t))
    }
    return pts
}

// ─── Aim State ───────────────────────────────────────────────────────────────
let isAiming        = false
let aimPlaneY       = 0
let aimCurrentDir   = new THREE.Vector3(1, 0, 0)
let aimCurrentPower = 0
let aimTargetDie    = null

const aimPlane     = new THREE.Plane()
const aimIntersect = new THREE.Vector3()

function updateAim(clientX, clientY) {
    pointer.x =  (clientX / window.innerWidth)  * 2 - 1
    pointer.y = -(clientY / window.innerHeight) * 2 + 1
    raycaster.setFromCamera(pointer, camera)
    if (!raycaster.ray.intersectPlane(aimPlane, aimIntersect)) return

    const diePos = aimTargetDie.mesh.position
    const dx = aimIntersect.x - diePos.x
    const dz = aimIntersect.z - diePos.z
    const dist = Math.sqrt(dx * dx + dz * dz)
    if (dist < 0.05) return

    const power = Math.min(dist / AIM_RING_RADIUS, 1)
    aimCurrentDir.set(dx / dist, 0, dz / dist)
    aimCurrentPower = power

    const clampedDist = Math.min(dist, AIM_RING_RADIUS)
    aimDot.position.set(diePos.x + aimCurrentDir.x * clampedDist, aimPlaneY, diePos.z + aimCurrentDir.z * clampedDist)

    const hue = (200 - power * 160) / 360
    const col = new THREE.Color().setHSL(hue, 0.9, 0.65)
    aimDotMat.color.copy(col)
    aimRingMat.color.copy(col)
    aimArrow.setColor(col)
    decayLineMat.color.copy(col)

    const arrowVec = new THREE.Vector3().subVectors(aimDot.position, diePos)
    const arrowLen = arrowVec.length()
    if (arrowLen > 0.05) {
        aimArrow.position.copy(diePos)
        aimArrow.setDirection(arrowVec.normalize())
        aimArrow.setLength(arrowLen, Math.min(0.3, arrowLen * 0.3), 0.15)
    }

    const decay = computeDecayArc(aimDot.position, aimCurrentDir, power)
    if (decay.length > 1) {
        decayLineGeo.setFromPoints(decay)
        decayLine.computeLineDistances()
    }

    hintEl.textContent = `Power: ${Math.round(power * 100)}%`
}

// ─── Interaction ─────────────────────────────────────────────────────────────
const raycaster = new THREE.Raycaster()
const pointer   = new THREE.Vector2()

canvas.addEventListener('pointerdown', (e) => {
    pointer.x =  (e.clientX / window.innerWidth)  * 2 - 1
    pointer.y = -(e.clientY / window.innerHeight) * 2 + 1
    raycaster.setFromCamera(pointer, camera)

    const hits = raycaster.intersectObjects(diceList.map(d => d.mesh))
    if (hits.length === 0) return

    aimTargetDie = hits[0].object.userData.dice

    isAiming = true
    aimCurrentPower = 0

    aimPlaneY = aimTargetDie.mesh.position.y + AIM_RING_Y
    aimPlane.set(new THREE.Vector3(0, 1, 0), -aimPlaneY)
    aimRing.position.set(aimTargetDie.mesh.position.x, aimPlaneY, aimTargetDie.mesh.position.z)

    setAimVisible(true)
    controls.enabled = false
    canvas.style.cursor = 'crosshair'
    e.preventDefault()
})

window.addEventListener('pointermove', (e) => {
    if (!isAiming) {
        pointer.x =  (e.clientX / window.innerWidth)  * 2 - 1
        pointer.y = -(e.clientY / window.innerHeight) * 2 + 1
        raycaster.setFromCamera(pointer, camera)
        const canThrow = diceList.some(d => !d.isThrown || d.isSettled)
        const hovering = raycaster.intersectObjects(diceList.map(d => d.mesh)).length > 0
        canvas.style.cursor = (hovering && canThrow) ? 'pointer' : 'default'
        return
    }
    updateAim(e.clientX, e.clientY)
})

window.addEventListener('pointerup', () => {
    if (!isAiming) return
    isAiming = false
    setAimVisible(false)
    controls.enabled = true
    canvas.style.cursor = 'default'

    if (aimCurrentPower > 0) {
        if (throwAllDice(aimCurrentDir, aimCurrentPower)) {
            resultEl.classList.remove('visible')
            hintEl.textContent = 'Rolling…'
        }
    } else {
        const allDone = diceList.every(d => d.isSettled)
        hintEl.textContent = allDone ? 'Press and drag to roll again' : 'Press and drag the die to roll'
    }
})

// ─── Camera ──────────────────────────────────────────────────────────────────
const sizes = { width: window.innerWidth, height: window.innerHeight }

const camera = new THREE.PerspectiveCamera(60, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 8, 12)
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.target.set(0, hh, 0)
controls.enableDamping = true
controls.minPolarAngle = 0
controls.maxPolarAngle = Math.PI * 0.82
controls.minDistance = 4
controls.maxDistance = 20

window.addEventListener('resize', () => {
    sizes.width  = window.innerWidth
    sizes.height = window.innerHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

// ─── Renderer ────────────────────────────────────────────────────────────────
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

// ─── Bounds check + clamp ─────────────────────────────────────────────────────
const _prevPos = new Map()   // body → {x,y,z} from last frame

function checkBounds() {
    diceList.forEach((d, idx) => {
        const hs   = d.type === 'd6' ? 0.5 : 0.65
        const p    = d.body.position
        const v    = d.body.velocity
        const prev = _prevPos.get(d.body) ?? { x: p.x, y: p.y, z: p.z }

        // Y is handled entirely by the physics floor/ceiling bodies.
        // Including y here fights those contacts and resets the sleep timer.
        // Use a margin so float drift near walls never triggers.
        const MARGIN = 0.15
        const walls = [
            { axis: 'x', clampLo: -hw + hs, clampHi:  hw - hs, checkLo: -hw + hs - MARGIN, checkHi:  hw - hs + MARGIN },
            { axis: 'z', clampLo: -hd + hs, clampHi:  hd - hs, checkLo: -hd + hs - MARGIN, checkHi:  hd - hs + MARGIN },
        ]

        const speed = Math.sqrt(v.x*v.x + v.y*v.y + v.z*v.z)

        walls.forEach(({ axis, clampLo, clampHi, checkLo, checkHi }) => {
            let side = null
            if (p[axis] < checkLo) side = 'lo'
            if (p[axis] > checkHi) side = 'hi'
            if (!side) return

            const limit = side === 'lo' ? clampLo : clampHi

            if (speed > 0.3) {
                console.warn(
                    `[escape] die#${idx} (${d.type}) ${axis}-${side}` +
                    ` pos=(${p.x.toFixed(2)},${p.y.toFixed(2)},${p.z.toFixed(2)})` +
                    ` prev=(${prev.x.toFixed(2)},${prev.y.toFixed(2)},${prev.z.toFixed(2)})` +
                    ` vel=(${v.x.toFixed(1)},${v.y.toFixed(1)},${v.z.toFixed(1)})` +
                    ` spd=${speed.toFixed(2)}`
                )
            }

            p[axis] = limit
            const movingOut = (side === 'lo' && v[axis] < 0) || (side === 'hi' && v[axis] > 0)
            if (movingOut) v[axis] *= -0.35
        })

        _prevPos.set(d.body, { x: p.x, y: p.y, z: p.z })
    })
}

// ─── Loop ────────────────────────────────────────────────────────────────────
const clock = new THREE.Clock()
let prevTime = 0

const tick = () => {
    const elapsed = clock.getElapsedTime()
    const delta = elapsed - prevTime
    prevTime = elapsed

    for (let i = 0; i < simSpeed; i++) {
        world.step(1 / 60, 1 / 60, 1)
        checkBounds()
    }
    diceList.forEach(d => d.update())
    controls.update()
    renderer.render(scene, camera)
    requestAnimationFrame(tick)
}

tick()
